import { gql } from '@apollo/client';
import { IntegrationSettings } from '../types/integration_settings/integrationSettings';
import { IntegrationSettingsInput } from '../types/integration_settings';
import { DeleteIntegrationSettingsInput } from '../types/integration_settings/integrationSettings.input';

const integrationSettings = gql`
  fragment IntegrationSettings on IntegrationSettings {
    id
    version
    companyId
    name
    type
    apiKey
    url
    tenantId
    environment
    plantId
    userId
    externalCompanyId
    externalInfo
  }
`;

const get = gql`
  query IntegrationSettings($companyId: String!) {
    integrationSettings(companyId: $companyId) {
      ...IntegrationSettings
    }
  }
  ${integrationSettings}
`;

export interface GetIntegrationSettingsVariables {
  companyId: string;
}

export interface GetIntegrationSettingsResponse {
  integrationSettings: IntegrationSettings[];
}

const createSAP = gql`
  mutation CreateSAPIntegration(
    $companyId: String!
    $name: String!
    $externalId: String!
    $apiKey: String!
    $url: String!
    $plantId: String!
  ) {
    createSAPIntegration(
      companyId: $companyId
      name: $name
      externalId: $externalId
      apiKey: $apiKey
      url: $url
      plantId: $plantId
    ) {
      ...IntegrationSettingsWithRelation
    }
  }
  ${integrationSettings}
`;

export interface CreateSAPIntegrationVariables {
  companyId: string;
  name: string;
  externalId: string;
  apiKey: string;
  url: string;
  plantId: string;
}

export interface CreateSAPIntegrationResponse {
  createSAPIntegration: IntegrationSettings;
}

const createBusinessCentral = gql`
  mutation CreateBusinessCentralIntegration(
    $companyId: String!
    $name: String!
    $tenantId: String!
    $environment: String!
    $clientId: String!
    $clientSecret: String!
    $externalId: String!
  ) {
    createBusinessCentralIntegration(
      companyId: $companyId
      name: $name
      tenantId: $tenantId
      environment: $environment
      clientId: $clientId
      clientSecret: $clientSecret
      externalId: $externalId
    ) {
      ...IntegrationSettingsWithRelation
    }
  }
  ${integrationSettings}
`;

export interface CreateBusinessCentralIntegrationVariables {
  companyId: string;
  name: string;
  tenantId: string;
  environment: string;
  clientId: string;
  clientSecret: string;
  externalId: string;
}

export interface CreateBusinessCentralIntegrationResponse {
  createBusinessCentralIntegration: IntegrationSettings;
}

const getBusinessCentralCompanies = gql`
  query BusinessCentralCompanies(
    $companyId: String!
    $tenantId: String!
    $environment: String!
    $clientId: String!
    $clientSecret: String!
  ) {
    businessCentralCompanies(
      companyId: $companyId
      tenantId: $tenantId
      environment: $environment
      clientId: $clientId
      clientSecret: $clientSecret
    ) {
      name
      displayName
      businessProfileId
      externalId
    }
  }
`;

export interface GetBusinessCentralCompaniesVariables {
  companyId: string;
  tenantId: string;
  environment: string;
  clientId: string;
  clientSecret: string;
}

export interface GetBusinessCentralCompaniesResponse {
  businessCentralCompanies: {
    name: string;
    displayName: string;
    businessProfileId: string;
    externalId: string;
  }[];
}

const update = gql`
  mutation UpdateIntegrationSettings($settings: [UpdateIntegrationSettingsInput!]!) {
    updateIntegrationSettings(settings: $settings) {
      ...IntegrationSettings
    }
  }
  ${integrationSettings}
`;

export interface UpdateIntegrationSettingsVariables {
  settings: IntegrationSettingsInput;
}

export interface UpdateIntegrationSettingsResponse {
  updateIntegrationSettings: IntegrationSettings[];
}

const remove = gql`
  mutation DeleteIntegrationSettings($settings: [DeleteIntegrationSettingsInput!]!) {
    deleteIntegrationSettings(settings: $settings) {
      ...IntegrationSettings
    }
  }
  ${integrationSettings}
`;

export interface DeleteIntegrationSettingsVariables {
  settings: DeleteIntegrationSettingsInput[];
}

export interface DeleteIntegrationSettingsResponse {
  deleteIntegrationSettings: IntegrationSettings[];
}

export const IntegrationSettingsQueries = {
  get,
  getBusinessCentralCompanies,
};

export const IntegrationSettingsMutations = {
  createSAP,
  createBusinessCentral,
  update,
  remove,
};

export const IntegrationSettingsFragments = {
  integrationSettings,
};
